import { Component, computed, effect, EffectRef, inject, Input, OnInit, QueryList, Signal, ViewChildren } from '@angular/core';
import { Search, SearchOption } from '../../../../../../models/types/components/Search.type';
import { SearchService } from '../../../../../../services/components/search.service';
import { SearchSelectComponent } from '../../../../../shared/search-select/search-select.component';
import { DvmFormatterPipe } from '../../../../../../helpers/pipes/dvm-formatter.pipe';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { orderOptions } from '@helpers/utils/order-options.util';
import { Translation } from '@models/types/components/Translation.type';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { TranslationService } from '@services/translation.service';

@Component({
    selector: 'app-search-advanced',
    imports: [
        SearchSelectComponent
    ],
    providers: [DvmFormatterPipe],
    templateUrl: './search-advanced.component.html',
    styleUrl: './search-advanced.component.css'
})
export class SearchAdvancedComponent implements OnInit {

  // INPUTS
  @Input({required: true})
  data: Search['fileData'];

  @ViewChildren(SearchSelectComponent) searchSelectComponents!: QueryList<SearchSelectComponent>;

  // SERVICES
  private readonly SearchService:    SearchService    = inject(SearchService);
  private readonly uiconfig:      Signal<UiConfiguration>  = inject(UiService).uiConfig;
  public  readonly translation:   TranslationService       = inject(TranslationService);
  

  //TRANSLATION CONFIG
  private readonly translationConfig:     Signal<Translation>         = computed((): Translation => this.uiconfig()['Translation']);
  public  readonly translationData:       Signal<Translation['data']> = computed((): Translation['data'] => this.translationConfig().data);
  public  readonly translationEnabled:    Signal<boolean>             = computed((): boolean  => this.translationConfig().enabled);
  public  readonly gaExceptions:          Signal<GeneralAdmission>    = computed((): GeneralAdmission=> this.uiconfig().GeneralAdmission);

  // PIPE
  private readonly dvmFormatter:          DvmFormatterPipe            = inject(DvmFormatterPipe);


  public  readonly order:                 Signal<Search['order']>     = computed((): Search['order']  => this.uiconfig().Search.order);
  

  // STATE
  protected activeSection:           string | null    = null;
  protected activeRow:               string | null    = null;
  protected activeSeat:              string | null    = null;

  public cssClasses:                 string[]         = ['w-[80px]','min-[432px]:w-[100px]'];

  private readonly onViewChange: EffectRef = effect((): void => {
    if (!this.SearchService.searchValue() && [this.activeSection, this.activeRow, this.activeSeat].some(v => v !== null)) {
      this.activeSection = null;
      this.activeRow = null;
      this.activeSeat = null;

      this.searchSelectComponents.forEach((component) => {
        component.resetInput();
      });
    }
  });

  // GETTERS
  public get sectionKeys(): SearchOption[] {
    if (!this.data) return [];

    const jsonOrder = Object.keys(this.data); // keep JSON order
    const translations = this.translationConfig().enabled ? this.translationData() : null;

    const options: SearchOption[] = jsonOrder.map(section => ({
      value: section,
      label: translations?.[section] || this.dvmFormatter.transform(section)
    }));

    return orderOptions(options, this.order(), jsonOrder);
  }

  public get rowKeys():              SearchOption[] {

    return this.data && this.activeSection ?
      Object.keys(this.data[this.activeSection ?? ''] ?? {}).map(row => ({value: row, label: row})) :
      [];

  };

  public get seatKeys():             SearchOption[] {
    
    return this.data && (this.activeSection && this.activeRow) ?
      this.data[this.activeSection ?? ''][this.activeRow ?? '']?.map(seat => ({value: seat, label: seat})) :
      [];

  };

  public get is3DSection(): boolean {
    return this.activeSection ? /\(INT\)(R|E)?/.test(this.activeSection) : false;
  }

  public get isGaException(): boolean {
    return this.activeSection? this.gaExceptions().enabled && this.gaExceptions().data.includes(this.activeSection): false;
  }


  // LIFE CYCLE HOOKS
  ngOnInit(): void {return void 0;}

  // METHODS
  public onFieldChange(field: 'section' | 'row' | 'seat', value: SearchOption): void {
    switch (field) {
      case 'section':
        this.activeSection = value.value;
        this.activeRow = null;
        this.activeSeat = null;
        if(this.is3DSection || this.isGaException) {
          this.startAdvancedSearch(true);
        }
        break;
      case 'row':
        this.activeRow = value.value;
        break;
      case 'seat':
        this.activeSeat = value.value;
        this.startAdvancedSearch();
        break;
    }
  }

  private startAdvancedSearch(load3d?: boolean): void {

    // Validamos que haya sección, fila y asiento seleccionados
    // o que se haya seleccionado una sección 3D
  if (!this.activeSection || (!this.activeRow || !this.activeSeat) && !load3d) return;

    // Creamos el valor de búsqueda y lo formateamos como un string de DVM (section_row-seat)
    const searchValue: string = `${this.activeSection}-${this.activeRow}-${this.activeSeat}`;

    // Actualizamos el valor de búsqueda en el servicio de búsqueda
    this.SearchService.setSearchValue({label: searchValue, value: this.activeSection});

    // Navegamos a la sección buscada
    window.setTimeout((): void => { 
      this.SearchService.navigateToSearch(this.activeSection!, searchValue);
    }, this.SearchService.duration + 250);

  }
 
}
